import { ChangeDetectionStrategy, Component, inject, signal, computed } from '@angular/core';
import { Dialog } from '@openng/optimus-ui/dialog';
import { Button } from '@openng/optimus-ui/button';
import { ServerErrorDialogService } from '../../../core/services/server-error-dialog.service';
import { environment } from 'src/environments/environment';

@Component({
    selector: 'server-error-dialog',
    changeDetection: ChangeDetectionStrategy.OnPush,
    templateUrl: './server-error-dialog.component.html',
    imports: [Dialog, Button]
})
export class ServerErrorDialogComponent {
    private readonly dialogService = inject(ServerErrorDialogService);

    readonly state = this.dialogService.state;
    readonly showDetails = signal(false);

    /**
     * In production we hide the SQL/stack-friendly summary by default and
     * surface only a user-readable message. Developers can still expand
     * "Ver detalles" to inspect everything.
     */
    readonly showTechnicalSummary = computed(() => !environment.production);

    readonly dialogHeader = computed(() =>
        environment.production ? 'Se ha producido un error' : 'Error del servidor'
    );

    readonly userMessage = computed(() => {
        const raw = this.state().message;
        if (!environment.production) {
            return raw || 'Error sin mensaje.';
        }
        // In production, never echo raw SQL/exception messages back to the
        // user. Show a friendly generic note plus the timestamp so they can
        // reference it on a support ticket.
        return 'No hemos podido completar la operación. Si el problema persiste, copia la hora indicada abajo y contacta con soporte.';
    });

    toggleDetails(): void {
        this.showDetails.update(value => !value);
    }

    close(): void {
        this.dialogService.hide();
        this.showDetails.set(false);
    }

    onVisibleChange(visible: boolean): void {
        if (!visible) {
            this.close();
        }
    }

    copyDetails(): void {
        const payload = this.buildAiPayload(this.state());

        if (navigator?.clipboard?.writeText) {
            navigator.clipboard.writeText(payload).catch(() => this.fallbackCopy(payload));
            return;
        }

        this.fallbackCopy(payload);
    }

    aiPayload(): string {
        return this.buildAiPayload(this.state());
    }

    private buildAiPayload(state: ReturnType<typeof this.state>): string {
        const interaction = state.lastInteraction;
        const stackTrace = state.stackTrace?.trim() || 'N/A';
        const timestamp = state.timestamp || new Date().toISOString();

        const lines = [
            '# Server Error Report',
            '',
            '## Summary',
            `Message: ${state.message || 'N/A'}`,
            `StatusCode: ${state.statusCode ?? 'N/A'}`,
            `ExceptionType: ${state.exceptionType || 'N/A'}`,
            `Timestamp: ${timestamp}`,
            '',
            '## Request',
            `Method: ${state.method || 'N/A'}`,
            `Url: ${state.url || window.location.href}`,
            '',
            '## Request Headers',
            '```json',
            JSON.stringify(state.requestHeaders ?? {}, null, 2),
            '```',
            '',
            '## Last User Interaction',
            `Type: ${interaction?.type || 'N/A'}`,
            `Target: ${interaction?.target || 'N/A'}`,
            `InputLength: ${interaction?.inputLength ?? 'N/A'}`,
            `InteractionTime: ${interaction?.timestamp || 'N/A'}`,
            `InteractionUrl: ${interaction?.url || 'N/A'}`,
            '',
            '## Client',
            `UserAgent: ${navigator.userAgent}`,
            `Language: ${navigator.language}`,
            '',
            '## Stack Trace',
            '```',
            stackTrace,
            '```'
        ];

        return lines.join('\n');
    }

    private fallbackCopy(payload: string): void {
        const textarea = document.createElement('textarea');
        textarea.value = payload;
        textarea.style.position = 'fixed';
        textarea.style.left = '-9999px';
        textarea.style.top = '0';
        document.body.appendChild(textarea);
        textarea.focus();
        textarea.select();
        document.execCommand('copy');
        document.body.removeChild(textarea);
    }
}
